import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";
import Tile from "@mapmycustomers/shared/types/territory/Tile";

import getBoundaryUrl from "@app/scene/map/utils/getBoundaryUrl";

interface LoadRequest {
  abortController: AbortController;
  areaType: AreaType;
  id?: string;
  promise: Promise<unknown>;
  tile?: Tile;
  zoomLevel: number;
}

export type TileData = {
  regionIds: AreaRegion["id"][];
  tile: Tile;
};

export class DataLoader {
  private loadRequests: LoadRequest[] = [];

  async loadRegionsForZoomLevel(
    areaType: AreaType,
    zoomLevel: number
  ): Promise<AreaRegion[] | undefined> {
    // check if we do process such request already
    const request = this.loadRequests.find(
      (request) =>
        request.areaType === areaType &&
        request.zoomLevel === zoomLevel &&
        !request.id &&
        !request.tile
    );
    // and if we do, just return its promise
    if (request) {
      return request.promise as Promise<AreaRegion[] | undefined>;
    }

    // keep loading requests for any other area types, but not this one
    // because if we wanna load data for the given zoom level, we don't need to
    // continue loading tiles/individual records
    // cancel any filtered out requests
    this.loadRequests = this.loadRequests.filter((request) => {
      if (request.areaType !== areaType) {
        return true;
      }
      request.abortController.abort();
      return false;
    });

    const abortController = new AbortController();
    const loadRequest: LoadRequest = {
      abortController,
      areaType,
      promise: (async (): Promise<AreaRegion[] | undefined> => {
        try {
          const response = await fetch(getBoundaryUrl("zoom", areaType, zoomLevel), {
            signal: abortController.signal,
          });
          const regions: AreaRegion[] = await response.json();
          if (Array.isArray(regions)) {
            return regions;
          }
        } catch (error) {
          console.error("error", error);
        } finally {
          // delete current request from the list
          this.loadRequests = this.loadRequests.filter((item) => item !== loadRequest);
        }
        return [];
      })(),
      zoomLevel,
    };
    this.loadRequests.push(loadRequest);

    return loadRequest.promise as Promise<AreaRegion[] | undefined>;
  }

  async loadDataForTiles(
    areaType: AreaType,
    zoomLevel: number,
    tiles: Tile[]
  ): Promise<TileData[]> {
    this.loadRequests = this.loadRequests.filter((request) => {
      // do not touch requests for other area types
      if (request.areaType !== areaType) {
        return true;
      }
      // cancel any other requests
      request.abortController.abort();
      return false;
    });

    const abortController = new AbortController();
    const request: LoadRequest = {
      abortController,
      areaType,
      promise: (async (): Promise<TileData[]> => {
        try {
          const response = await fetch(getBoundaryUrl("tiles", areaType), {
            body: JSON.stringify({ areaType, tiles }),
            headers: { "Content-Type": "application/json" },
            method: "POST",
            signal: abortController.signal,
          });
          return await response.json();
        } catch (error) {
          console.error("Failed to load tiles", error);
        }
        return [];
      })(),
      zoomLevel,
    };
    this.loadRequests.push(request);

    return request.promise as Promise<TileData[]>;
  }

  async loadTileRegions(areaType: AreaType, zoomLevel: number, regionIds: AreaRegion["id"][]) {
    try {
      const response = await fetch(getBoundaryUrl("regions", areaType), {
        body: JSON.stringify({ areaType, regionIds, zoom: zoomLevel }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      const regions: AreaRegion[] = await response.json();
      return regions;
    } catch (error) {
      console.error("Failed to load regions", error);
      return [];
    }
  }
}

const dataLoader = new DataLoader();

export default dataLoader;
